import React, { useState, useEffect } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import searchIcon from '../../assets/icons/search.svg';
import { useAuth } from '@/contexts/AuthContext';
import { INPUT_THEMES, type InputTheme } from './form/inputThemes';

interface Partner {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface PartnerAutocompleteProps {
  value: Partner | string | null;
  onChange: (partner: Partner | string | null) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  /** Visual theme: 'dark' or 'light'. Defaults to 'dark'. */
  theme?: InputTheme;
}

export function PartnerAutocomplete({
  value,
  onChange,
  placeholder = "Type a name or a username",
  className = "",
  containerClassName = "",
  disabled = false,
  theme = 'dark'
}: PartnerAutocompleteProps) {
  const { user } = useAuth();
  const tk = INPUT_THEMES[theme];
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [showManualOption, setShowManualOption] = useState(false);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.full_name : '');

  useEffect(() => {
    setInputValue(displayValue || '');
  }, [displayValue]);

  // Reset input when value becomes null (after partner selection)
  useEffect(() => {
    if (value === null) {
      setInputValue('');
      setOpen(false);
      setShowManualOption(false);
    }
  }, [value]);

  // Scroll to dropdown when it opens
  useEffect(() => {
    const safeFilteredPartners = filteredPartners || [];
    if (open && (safeFilteredPartners.length > 0 || showManualOption)) {
      const dropdown = document.querySelector('[data-partner-dropdown]');
      if (dropdown && dropdown instanceof Element && typeof dropdown.scrollIntoView === 'function') {
        try {
          dropdown.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        } catch (error) {
          // Silently ignore scroll errors in test environment
          console.warn('Failed to scroll dropdown:', error);
        }
      }
    }
  }, [open, filteredPartners, showManualOption]);

  useEffect(() => {
    const fetchPartners = async () => {
      const safeInputValue = inputValue || '';
      if (!safeInputValue || safeInputValue.length < 2 || !user) {
        setFilteredPartners([]);
        setShowManualOption(false);
        return;
      }
      
      setLoading(true);
      try {
        // Get the session token
        const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
        const token = session?.access_token;

        if (!token) {
          console.error('❌ [PartnerAutocomplete] No token available');
          setFilteredPartners([]);
          setShowManualOption(false);
          setLoading(false);
          return;
        }

        const { getEdgeFunctionUrl } = await import('@/utils/url');
        const functionUrl = getEdgeFunctionUrl('search_partners');
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            searchTerm: safeInputValue,
            limit: 10
          })
        });

        if (response.ok) {
          const result = await response.json();
          const partners = result.data || [];
          setFilteredPartners(partners);
          
          // Show manual option if no exact matches found
          const hasExactMatch = partners.some(
            (partner: Partner) => 
              (partner.full_name?.toLowerCase() === inputValue.toLowerCase()) ||
              (partner.username?.toLowerCase() === inputValue.toLowerCase())
          );
          const shouldShowManual = !hasExactMatch && safeInputValue.length > 0;
          setShowManualOption(shouldShowManual);
        } else {
          const errorText = await response.text();
          console.error('❌ [PartnerAutocomplete] Error fetching partners:', response.status, errorText);
          setFilteredPartners([]);
          setShowManualOption(false);
        }
      } catch (error) {
        console.error('❌ [PartnerAutocomplete] Error fetching partners:', error);
        setFilteredPartners([]);
        setShowManualOption(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPartners, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, user]);

  const handleSelect = (partner: Partner | string) => {
    onChange(partner);
    setInputValue(typeof partner === 'string' ? partner : partner.full_name || '');
    setOpen(false);
    setShowManualOption(false);
  };

  const handleManualAdd = () => {
    const safeInputValue = inputValue || '';
    onChange(safeInputValue);
    setInputValue(safeInputValue);
    setOpen(false);
    setShowManualOption(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    setInputValue(value);
    setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            theme === 'light'
              ? "brightness-0 opacity-40"
              : "text-white/60"
          )}
        />
        <input
          type="text"
          value={inputValue || ''}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "w-full border rounded-lg pl-12 pr-12 text-xs focus:outline-none transition-all duration-200 font-sans h-11",
            tk.input.split(' ').filter(cls => !cls.startsWith('h-') && !cls.startsWith('text-') && !cls.startsWith('font-')).join(' '),
            className
          )}
          style={{
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        />
        {inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              onChange(null);
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              theme === 'light'
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {loading && !inputValue && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ zIndex: 2 }}>
            <div className={cn(
              "w-4 h-4 border-2 rounded-full animate-spin",
              theme === 'light'
                ? "border-gray-300 border-t-gray-600"
                : "border-white/30 border-t-white"
            )} />
          </div>
        )}
      </div>

      {(() => {
        // Ensure all values are safe to prevent undefined errors
        const safeFilteredPartners = filteredPartners || [];
        const safeInputValue = inputValue || '';
        const safeShowManualOption = showManualOption || false;
        const shouldShow = open && safeInputValue.length > 0 && (safeFilteredPartners.length > 0 || safeShowManualOption);
        return shouldShow;
      })() && (
        <div className={['absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto', tk.dropdown].join(' ')} data-partner-dropdown data-testid="partner-dropdown">
          {loading ? (
            <div className={tk.helperText + ' p-4 text-center'}>
              Loading partners...
            </div>
          ) : (
            <div className="py-1">
              {(filteredPartners || []).map((partner) => (
                <div
                  key={partner.id}
                  onClick={() => handleSelect(partner)}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {inputValue === partner.full_name && <Check className="h-4 w-4 text-white" />}
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">{partner.full_name || 'Unknown'}</span>
                    <span className="text-xs text-white/60">@{partner.username || 'unknown'}</span>
                  </div>
                </div>
              ))}
              
              {showManualOption && (
                <div
                  onClick={handleManualAdd}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10 border-t border-white/20"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Plus className="h-4 w-4 text-white" />
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">
                      Add "{inputValue}" manually
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 